import {FC, useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {getMyNotifications, markNotificationsSeen, NotificationTicket} from './tickets/core/_requests'
import {DEPARTMENTS, PRIORITY_LABELS} from './tickets/core/_models'

const PRIORITY_STYLE: Record<string, {bg: string; color: string; label: string}> = {
  urgent: {bg: 'rgba(246,78,96,0.12)', color: '#f64e60', label: PRIORITY_LABELS.urgent},
  high: {bg: 'rgba(255,168,0,0.12)', color: '#ff9800', label: PRIORITY_LABELS.high},
  medium: {bg: 'rgba(54,153,255,0.12)', color: '#3699ff', label: PRIORITY_LABELS.medium},
  low: {bg: 'rgba(126,130,153,0.12)', color: '#7e8299', label: PRIORITY_LABELS.low},
}

const deptLabel = (v: string | null) => (v ? DEPARTMENTS.find((d) => d.value === v)?.label || v : '')

// The bell is also rendered inside the client-facing layout (when an admin is
// "logged in as" a customer). It must only appear for a valid, non-expired admin
// token — never for a regular client. On the admin panel this is always true.
const hasValidAdminToken = (): boolean => {
  try {
    const t = localStorage.getItem('admin_auth_token')
    if (!t) return false
    const payload = JSON.parse(atob(t.split('.')[1]))
    if (payload?.role !== 'admin') return false
    if (payload?.exp && payload.exp * 1000 <= Date.now()) return false
    return true
  } catch {
    return false
  }
}

// Relative time in Hebrew (compact).
const relTime = (iso: string): string => {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (diff < 60) return 'עכשיו'
  const m = Math.floor(diff / 60)
  if (m < 60) return `לפני ${m} ד׳`
  const h = Math.floor(m / 60)
  if (h < 24) return `לפני ${h} ש׳`
  const d = Math.floor(h / 24)
  return `לפני ${d} י׳`
}

// Header notification bell: shows a badge with the number of newly-assigned
// (unseen) tickets, and a dropdown listing the current admin's most important
// open tasks (new first, then by priority, then oldest). Opening the dropdown
// marks them as seen; clicking a task opens it.
const NotificationBell: FC = () => {
  const navigate = useNavigate()
  const isAdmin = hasValidAdminToken()
  const [open, setOpen] = useState(false)
  const [newCount, setNewCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [tickets, setTickets] = useState<NotificationTicket[]>([])
  const [loading, setLoading] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const prevNew = useRef(0)

  const fetchNotifs = useCallback(async () => {
    if (!isAdmin) return
    try {
      const res = await getMyNotifications()
      setNewCount(res.data.newCount || 0)
      setTotal(res.data.total || 0)
      setTickets(res.data.tickets || [])
    } catch {
      // silently fail
    }
  }, [isAdmin])

  // Poll — faster while there are new items.
  useEffect(() => {
    fetchNotifs()
    const interval = setInterval(fetchNotifs, newCount > 0 ? 20000 : 60000)
    return () => clearInterval(interval)
  }, [fetchNotifs, newCount])

  // Browser notification when the new-count rises.
  useEffect(() => {
    if (newCount > prevNew.current && prevNew.current >= 0 && 'Notification' in window) {
      if (Notification.permission === 'default') Notification.requestPermission()
      if (Notification.permission === 'granted' && newCount > 0) {
        new Notification('פנייה חדשה הוקצתה אליך', {
          body: `יש לך ${newCount} פניות חדשות ממתינות`,
          tag: 'ticket-assigned',
          icon: '/favicon.ico',
        })
      }
    }
    prevNew.current = newCount
  }, [newCount])

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggle = async () => {
    const next = !open
    setOpen(next)
    if (next) {
      setLoading(true)
      await fetchNotifs()
      setLoading(false)
      // Mark seen so the badge clears; keep the list's "new" flags for this view.
      if (newCount > 0) {
        markNotificationsSeen().then(() => setNewCount(0)).catch(() => {})
      }
    }
  }

  const goto = (id: string) => {
    setOpen(false)
    navigate(`/admin/tickets/${id}`)
  }

  // Only render for a valid admin (e.g. when an admin is "logged in as" a client).
  if (!isAdmin) return null

  return (
    <div ref={ref} style={{position: 'relative', marginLeft: '4px'}}>
      <button
        onClick={toggle}
        title="התראות"
        style={{
          position: 'relative', width: '38px', height: '38px', borderRadius: '10px',
          border: '1px solid #e4e6ef', background: open ? '#f1eefe' : '#fff',
          color: open ? '#7239ea' : '#5e6278', cursor: 'pointer', fontSize: '17px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <i className={`bi ${newCount > 0 ? 'bi-bell-fill' : 'bi-bell'}`}></i>
        {newCount > 0 && (
          <span style={{
            position: 'absolute', top: '-5px', right: '-5px', minWidth: '18px', height: '18px',
            borderRadius: '9px', background: '#f64e60', color: '#fff', fontSize: '10px',
            fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}>
            {newCount > 99 ? '99+' : newCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '46px', right: 0, width: '360px', maxHeight: '480px',
          background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          zIndex: 1200, overflow: 'hidden', display: 'flex', flexDirection: 'column', direction: 'rtl',
        }}>
          <div style={{padding: '14px 18px', borderBottom: '1px solid #eef0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{fontWeight: 700, fontSize: '15px', color: '#1e1e2d'}}>
              <i className="bi bi-bell" style={{marginLeft: '8px', color: '#7239ea'}}></i>
              המשימות שלי
            </div>
            <span style={{background: '#f1eefe', color: '#7239ea', padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600}}>
              {total} פתוחות
            </span>
          </div>

          <div style={{flex: 1, overflowY: 'auto'}}>
            {loading ? (
              <div style={{padding: '30px', textAlign: 'center', color: '#a1a5b7'}}>
                <div className="spinner-border spinner-border-sm" />
              </div>
            ) : tickets.length === 0 ? (
              <div style={{padding: '32px 20px', textAlign: 'center', color: '#a1a5b7'}}>
                <i className="bi bi-check2-circle" style={{fontSize: '30px', display: 'block', marginBottom: '8px'}}></i>
                אין משימות פתוחות
              </div>
            ) : (
              tickets.map((t) => {
                const p = PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.medium
                const acc = t.account ? `${t.account.first_name || ''} ${t.account.last_name || ''}`.trim() : ''
                return (
                  <div
                    key={t._id}
                    onClick={() => goto(t._id)}
                    style={{
                      padding: '11px 18px', borderBottom: '1px solid #f4f5f8', cursor: 'pointer',
                      background: t.is_new ? 'rgba(114,57,234,0.04)' : '#fff',
                      display: 'flex', gap: '10px', alignItems: 'flex-start',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fc')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = t.is_new ? 'rgba(114,57,234,0.04)' : '#fff')}
                  >
                    <span style={{
                      marginTop: '6px', width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                      background: t.is_new ? '#7239ea' : 'transparent',
                    }} />
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px'}}>
                        <span style={{
                          fontSize: '13px', fontWeight: t.is_new ? 700 : 600, color: '#1e1e2d',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {t.subject}
                        </span>
                      </div>
                      <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                        <span style={{fontSize: '10px', fontWeight: 700, background: p.bg, color: p.color, padding: '1px 7px', borderRadius: '4px'}}>
                          {p.label}
                        </span>
                        {t.department && (
                          <span style={{fontSize: '11px', color: '#7e8299'}}>
                            <i className="bi bi-diagram-3" style={{marginLeft: '3px'}}></i>{deptLabel(t.department)}
                          </span>
                        )}
                        {acc && (
                          <span style={{fontSize: '11px', color: '#3699ff'}}>
                            <i className="bi bi-person" style={{marginLeft: '3px'}}></i>{acc}
                          </span>
                        )}
                        <span style={{fontSize: '11px', color: '#a1a5b7', marginRight: 'auto'}}>{relTime(t.created_at)}</span>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          <div
            onClick={() => {
              setOpen(false)
              navigate('/admin/tickets?my_tickets=true&status=active')
            }}
            style={{padding: '12px', borderTop: '1px solid #eef0f4', textAlign: 'center', cursor: 'pointer', color: '#7239ea', fontWeight: 600, fontSize: '13px'}}
          >
            צפה בכל הפניות שלי
            <i className="bi bi-arrow-left" style={{marginRight: '6px'}}></i>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
